// Emailed sign-in codes: one place for the send call that /login (single-sso) makes and
// /confirm-sso's "Send a new code" repeats, so both always ask the same backend.

/**
 * The auth backend (dev/staging/prod) for this page, picked from the hostname only. /confirm-sso checks
 * the code with the same rule. VITE_APP_WORKSPACE picks the race-data workspace, not the auth backend:
 * a local .env with VITE_APP_WORKSPACE=prod sent the code from prod while /confirm-sso checked it in dev.
 * @param {string} [hostname]
 * @returns {"dev" | "staging" | "prod"}
 */
export function getAuthWorkspace(hostname = window.location.hostname) {
  if (hostname === "staging.lexi.tips") return "staging";
  if (hostname === "lexi.tips") return "prod";
  return "dev";
}

/**
 * Ask the backend to email a sign-in code. Uses /api/send-verification, falling back to the Cloud Run
 * function on a 404; on localhost (where Vite doesn't serve /api) it goes straight to the function.
 * @param {string} email
 * @param {{ hostname?: string }} [options]
 * @returns {Promise<{ sid?: string, success?: boolean, message?: string, error?: string }>} the backend's
 *   JSON (empty object if the body isn't JSON). A code was sent when `sid` or `success` is set.
 */
export async function requestSignInCode(
  email,
  { hostname = window.location.hostname } = {},
) {
  const normalizedEmail = email.trim().toLowerCase();
  const cfUrl = `https://lexi-${getAuthWorkspace(hostname)}-send-verification-mcdr62tc5a-ts.a.run.app`;

  const doCall = (url) =>
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: normalizedEmail,
        type: "email",
        mode: "signin",
      }),
    });

  // In localhost, Vite doesn't serve /api; go straight to CF
  if (hostname === "localhost") {
    console.warn(
      "Localhost detected, using CF directly for send-verification:",
      cfUrl,
    );
    const direct = await doCall(cfUrl);
    return direct.json().catch(() => ({}));
  }

  let resp = await doCall("/api/send-verification");
  if (resp.status === 404) {
    console.warn(
      "/api/send-verification not found locally, falling back to CF:",
      cfUrl,
    );
    resp = await doCall(cfUrl);
  }
  return resp.json().catch(() => ({}));
}

/** True when the backend's reply says a code is on its way. */
export const codeWasSent = (result) => Boolean(result?.sid || result?.success);

/**
 * The code in pasted text: the first run of exactly six digits, so "Your Lexi code is 482913. It expires
 * in 10 minutes" gives "482913". Null when there's no such run (e.g. "482 913"). No lookbehind: older
 * iPhone Safari can't parse it.
 * @param {string} text
 * @returns {string | null}
 */
export function findCodeInText(text) {
  const match = /(?:^|\D)(\d{6})(?!\d)/.exec(text ?? "");
  return match ? match[1] : null;
}

/** john.doe@gmail.com -> jo***@gmail.com (what the page shows instead of the full address). */
export function maskEmail(email) {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  const maskedLocal =
    localPart.length > 2 ? localPart.slice(0, 2) + "***" : localPart + "***";
  return `${maskedLocal}@${domain}`;
}
